/* External */
import {
  useState, useEffect, useCallback, useRef,
} from 'react';

/* Utils */
import getToolboxRegistrationCode from './getToolboxRegistrationCode';

const MAX_NUMBER_OF_ATTEMPTS = 10;
const RETRY_INTERVAL_TIME = 5000;

export default function useRegistrationCode(country) {
  const [registrationCode, setRegistrationCode] = useState('');
  const [deviceCode, setDeviceCode] = useState('');
  const [expirationTimeCode, setExpirationTimeCode] = useState(0);
  const [numberOfAttempts, setNumberOfAttempts] = useState(0);
  const [errorText, setErrorText] = useState('');
  const registrationCodeIntervalId = useRef();
  const registrationCodeRef = useRef();
  const showError = !registrationCode && numberOfAttempts === MAX_NUMBER_OF_ATTEMPTS;
  const fetchRegistrationCode = useCallback(async () => {
    if (country) {
      const newRegistrationCode = await getToolboxRegistrationCode();
      const {
        userCode,
        expiresIn,
        deviceCode: codeDeviceService,
        errorMessage,
      } = newRegistrationCode;
      if (newRegistrationCode) {
        setDeviceCode(codeDeviceService);
        setExpirationTimeCode(expiresIn * 1000);
        setRegistrationCode(userCode);
        setErrorText(errorMessage);
      }
      setNumberOfAttempts((currentNumberOfAttempts) => currentNumberOfAttempts + 1);
    }
  }, [country]);

  const resetNumberOfAttempts = useCallback(() => {
    setNumberOfAttempts(0);
  }, []);

  const clearIntervalRegistrationCode = useCallback(() => {
    if (registrationCodeIntervalId.current) {
      clearInterval(registrationCodeIntervalId.current);
    }
  }, []);

  const resetCallFetchRegistrationCode = useCallback(() => {
    clearIntervalRegistrationCode();
    setDeviceCode('');
    setExpirationTimeCode(0);
    setRegistrationCode('');
    resetNumberOfAttempts();
  }, [clearIntervalRegistrationCode, resetNumberOfAttempts]);

  useEffect(() => {
    if (expirationTimeCode) {
      registrationCodeIntervalId.current = setInterval(
        resetCallFetchRegistrationCode,
        expirationTimeCode,
      );
    }
  }, [expirationTimeCode, resetCallFetchRegistrationCode]);

  useEffect(() => {
    const currentClearIntervalRegistrationCode = clearIntervalRegistrationCode;
    return () => {
      currentClearIntervalRegistrationCode();
    };
  }, [clearIntervalRegistrationCode]);

  registrationCodeRef.current = registrationCode;

  useEffect(() => {
    let timeout;
    if (!registrationCodeRef.current && numberOfAttempts === 0) {
      fetchRegistrationCode();
    } else if (!registrationCodeRef.current && numberOfAttempts < MAX_NUMBER_OF_ATTEMPTS) {
      timeout = setTimeout(fetchRegistrationCode, RETRY_INTERVAL_TIME);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [numberOfAttempts, fetchRegistrationCode]);

  return {
    registrationCode,
    deviceCode,
    showError,
    errorText,
    resetNumberOfAttempts,
  };
}
